import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ChevronDown, ChevronRight } from "lucide-react";

const NEXT_STATUS = {
  assigned: "received",
  received: "in_progress",
  in_progress: "repair_done",
  repair_done: "sent_back",
};
const TRANSITION_LABEL = {
  received: "Enhet mottatt",
  in_progress: "Reparasjon påbegynt",
  repair_done: "Reparasjon fullført",
  sent_back: "Sendt tilbake",
};
const STATUS_LABEL = {
  assigned: "Betalt — venter på enhet",
  received: "Enhet mottatt",
  in_progress: "Under reparasjon",
  repair_done: "Reparasjon fullført",
  sent_back: "Sendt tilbake",
  completed: "Fullført",
  paid_out: "Utbetalt",
};

const ACTIVE_STATUSES = new Set(["assigned", "received", "in_progress"]);
const WAITING_PAYOUT_STATUSES = new Set(["repair_done", "sent_back", "completed"]);
const PAID_OUT_STATUSES = new Set(["paid_out"]);

export default function FixerJobs() {
  const [orders, setOrders] = useState([]);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const refresh = useCallback(() => {
    api.get("/fixer/orders").then((r) => setOrders(r.data));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const advance = async (o) => {
    const next = NEXT_STATUS[o.status];
    if (!next) return;
    try {
      await api.patch(`/fixer/orders/${o.id}/status`, { status: next });
      toast.success(`Status oppdatert: ${TRANSITION_LABEL[next]}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const regular = orders.filter((o) => !o.is_diagnostic);
  const aktive = regular.filter((o) => ACTIVE_STATUSES.has(o.status));
  const venterUtbetaling = regular.filter((o) => WAITING_PAYOUT_STATUSES.has(o.status));
  const utbetalt = regular.filter((o) => PAID_OUT_STATUSES.has(o.status));

  return (
    <div className="tk-container py-16" data-testid="fixer-jobs-page">
      <p className="tk-overline mb-3">Oppdrag</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-10">Innkommende bestillinger</h1>

      <Section
        title="Aktive oppdrag"
        subtitle="Pågående reparasjoner og enheter på vei"
        orders={aktive}
        onAdvance={advance}
        testid="section-aktive"
        emptyText="Ingen aktive oppdrag."
      />

      <Section
        title="Fullført — venter på utbetaling"
        subtitle="Avsluttede oppdrag som ikke er utbetalt fra Tekkdeler ennå"
        orders={venterUtbetaling}
        onAdvance={advance}
        testid="section-venter"
        emptyText="Ingen fullførte oppdrag som venter på utbetaling."
      />

      {/* Archive — collapsed by default to keep the list clean */}
      <section className="mt-12" data-testid="section-utbetalt">
        <button
          onClick={() => setArchiveOpen((o) => !o)}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0B1C30] mb-4"
          data-testid="archive-toggle"
        >
          {archiveOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="font-semibold">Utbetalt ({utbetalt.length})</span>
          <span className="text-xs text-slate-400">— arkivert</span>
        </button>
        {archiveOpen && (
          <OrdersTable orders={utbetalt} onAdvance={advance} archived />
        )}
      </section>
    </div>
  );
}

function Section({ title, subtitle, orders, onAdvance, testid, emptyText }) {
  return (
    <section className="mb-12" data-testid={testid}>
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
      {orders.length === 0 ? (
        <p className="text-sm text-slate-500">{emptyText}</p>
      ) : (
        <OrdersTable orders={orders} onAdvance={onAdvance} />
      )}
    </section>
  );
}

function OrdersTable({ orders, onAdvance, archived = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="fixer-orders-table">
        <thead className="text-left">
          <tr className="border-b border-[#E2E8F0]">
            <th className="py-3 pr-4 font-medium text-slate-500">Ordre</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Produkt</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Reparasjon</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Levering</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Arbeid</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Status</th>
            <th className="py-3 pr-4 font-medium text-slate-500"></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const next = NEXT_STATUS[o.status];
            return (
              <tr
                key={o.id}
                className={`border-b border-[#E2E8F0]/70 ${archived ? "opacity-70" : ""}`}
                data-testid={`fixer-order-${o.id}`}
              >
                <td className="py-3 pr-4 font-mono text-xs">
                  <Link
                    to={`/verksted/jobb/${o.id}`}
                    className="text-slate-400 hover:text-[#0B1C30]"
                    data-testid={`job-link-${o.id}`}
                  >
                    #{o.id.slice(0, 8)} ↗
                  </Link>
                </td>
                <td className="py-3 pr-4">{o.product_name}</td>
                <td className="py-3 pr-4">{o.service_name}</td>
                <td className="py-3 pr-4 text-xs">
                  {o.delivery_method === "shipping" ? "Bring" : "Drop-off"}
                </td>
                <td className="py-3 pr-4 font-medium">kr {Number(o.service_fee).toFixed(0)}</td>
                <td className="py-3 pr-4">
                  <span className="text-xs px-2 py-1 rounded-md bg-slate-100">
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  {!archived && next && (
                    <button
                      onClick={() => onAdvance(o)}
                      className="tk-btn-secondary !py-1.5 !px-3 text-xs"
                      data-testid={`advance-${o.id}`}
                    >
                      → {TRANSITION_LABEL[next]}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
